import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import { useT } from '../../context/LanguageContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import loyaltyService from '../../services/loyaltyService';
import { CreditCard, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';

/*
 * Gift cards — stored value the restaurant has been PAID for. The register
 * shows the outstanding liability beside the list because that figure is the
 * whole point of the register; the card drawer is the till's side: look one
 * up by code, spend from it, top it up, or (deliberately, with a warning)
 * void it.
 */

const STATUS_CLS = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  REDEEMED: 'bg-gray-100 text-gray-500',
  EXPIRED: 'bg-amber-100 text-amber-700',
  VOID: 'bg-red-50 text-red-600',
  INACTIVE: 'bg-gray-100 text-gray-500',
};

const EMPTY_ISSUE = {
  face_value: '', sold_amount: '', payment_method: 'CASH',
  purchaser_name: '', purchaser_phone: '',
  recipient_name: '', recipient_phone: '', message: '', validity_days: 365,
};

export default function GiftCardsPage() {
  const { t } = useT();
  const { toast } = useToast();
  const { formatCurrency, formatDate } = useAppSettings();
  const money = (v) => formatCurrency(Number(v) || 0);

  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueBusy, setIssueBusy] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_ISSUE });

  // The card drawer (till side)
  const [card, setCard] = useState(null);
  const [amount, setAmount] = useState('');
  const [voidAsk, setVoidAsk] = useState(false);

  const load = useCallback((query, pageNo) => {
    loyaltyService.listGiftCards({ q: query || undefined, page: pageNo, page_size: 20 })
      .then(setData)
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }));
  }, [toast]);

  useEffect(() => { load('', 1); }, [load]);

  const search = (e) => { e?.preventDefault(); setPage(1); load(q, 1); };
  const goto = (p) => { setPage(p); load(q, p); };

  const openCard = async (code) => {
    setVoidAsk(false);
    setAmount('');
    try {
      setCard(await loyaltyService.giftCardLookup(code));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const issue = async (e) => {
    e?.preventDefault();
    setIssueBusy(true);
    try {
      const c = await loyaltyService.issueGiftCard({
        ...form,
        sold_amount: form.sold_amount || undefined,
        validity_days: form.validity_days || 0,
      });
      toast({ title: t('loyalty.gc.sell'), description: `${c.code} · ${money(c.face_value)}` });
      setIssueOpen(false);
      setForm({ ...EMPTY_ISSUE });
      load(q, page);
      setCard(c);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally { setIssueBusy(false); }
  };

  const act = async (fn, title) => {
    try {
      const c = await fn();
      setCard(c);
      setAmount('');
      setVoidAsk(false);
      load(q, page);
      if (title) toast({ title, description: `${c.code} · ${money(c.amount_remaining)}` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 w-full">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
            <CreditCard className="h-7 w-7 text-amber-600" /> {t('loyalty.giftCards')}
          </h1>
          {data && (
            <p className="text-gray-600 mt-1">
              {t('loyalty.gc.liability')}: <strong>{money(data.outstanding_liability)}</strong>
            </p>
          )}
        </div>
        <Button onClick={() => setIssueOpen(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          <Plus size={15} className="mr-1" /> {t('loyalty.gc.sell')}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <form onSubmit={search} className="flex gap-2 max-w-md mb-4">
            <Input placeholder={`${t('loyalty.gc.lookup')} / ${t('loyalty.common.name')} / ${t('loyalty.common.phone')}`}
              value={q} onChange={(e) => setQ(e.target.value)} />
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
              <Search size={15} />
            </Button>
          </form>

          {!data ? <p className="text-sm text-gray-500">{t('loyalty.common.loading')}</p>
            : data.results.length === 0 ? (
              <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-gray-500">
                      <tr>
                        <th className="text-left font-medium py-2 pr-4">Code</th>
                        <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.name')}</th>
                        <th className="text-right font-medium py-2 pr-4">{t('loyalty.member.balance')}</th>
                        <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.status')}</th>
                        <th className="text-left font-medium py-2">{t('loyalty.common.when')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.results.map((c) => (
                        <tr key={c.id} className="cursor-pointer hover:bg-accent/50"
                          onClick={() => openCard(c.code)}>
                          <td className="py-2 pr-4 font-mono text-xs">{c.code}</td>
                          <td className="py-2 pr-4">
                            <div className="font-medium text-gray-900">{c.recipient_name || c.purchaser_name || '—'}</div>
                            <div className="text-xs text-gray-400">{c.recipient_phone || c.purchaser_phone || ''}</div>
                          </td>
                          <td className="py-2 pr-4 text-right tabular-nums">
                            <span className="font-semibold">{money(c.amount_remaining)}</span>
                            <span className="text-gray-400 text-xs"> / {money(c.face_value)}</span>
                          </td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[c.status] || ''}`}>
                              {t(`loyalty.gc.status.${c.status}`)}
                            </span>
                          </td>
                          <td className="py-2">{formatDate(c.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.total_pages > 1 && (
                  <div className="mt-3 flex items-center justify-end gap-2 text-sm text-gray-600">
                    <button type="button" disabled={page <= 1} onClick={() => goto(page - 1)}
                      className="p-1 rounded hover:bg-accent disabled:opacity-40"><ChevronLeft size={16} /></button>
                    <span className="tabular-nums">{page} / {data.total_pages}</span>
                    <button type="button" disabled={page >= data.total_pages} onClick={() => goto(page + 1)}
                      className="p-1 rounded hover:bg-accent disabled:opacity-40"><ChevronRight size={16} /></button>
                  </div>
                )}
              </>
            )}
        </Card>

        {/* The card drawer */}
        <Card className="p-5">
          {!card ? (
            <p className="text-sm text-gray-500">{t('loyalty.gc.lookup')}</p>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-semibold">{card.code}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[card.status] || ''}`}>
                  {t(`loyalty.gc.status.${card.status}`)}
                </span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3">
                <div className="text-xs text-gray-500">{t('loyalty.member.balance')}</div>
                <div className="text-2xl font-semibold text-gray-900">{money(card.amount_remaining)}</div>
                <div className="text-[11px] text-gray-400">/ {money(card.face_value)}</div>
              </div>
              {(card.recipient_name || card.purchaser_name) && (
                <div className="text-gray-600">
                  {card.recipient_name || card.purchaser_name}
                  {card.message ? <div className="text-xs text-gray-400 italic mt-0.5">“{card.message}”</div> : null}
                </div>
              )}
              {card.expires_at && (
                <div className="text-gray-500 text-xs">Expires {formatDate(card.expires_at)}</div>
              )}

              {card.status === 'ACTIVE' && (
                <div className="space-y-2 pt-1">
                  <Input type="number" step="0.01" min="0.01" placeholder="Rs"
                    value={amount} onChange={(e) => setAmount(e.target.value)} />
                  <div className="flex gap-2">
                    <Button size="sm" disabled={!amount}
                      onClick={() => act(() => loyaltyService.redeemGiftCard({ code: card.code, amount }),
                        t('loyalty.gc.redeem'))}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {t('loyalty.gc.redeem')}
                    </Button>
                    <Button size="sm" variant="outline" disabled={!amount}
                      onClick={() => act(() => loyaltyService.topUpGiftCard({ code: card.code, amount }),
                        t('loyalty.gc.topup'))}>
                      {t('loyalty.gc.topup')}
                    </Button>
                  </div>
                </div>
              )}

              {card.status !== 'VOID' && (
                !voidAsk ? (
                  <button type="button" onClick={() => setVoidAsk(true)}
                    className="text-xs text-red-600 hover:underline">{t('loyalty.gc.void')}</button>
                ) : (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-red-700">{t('loyalty.gc.voidWarn')}</span>
                    <button type="button"
                      onClick={() => act(() => loyaltyService.voidGiftCard(card.id, ''), t('loyalty.gc.void'))}
                      className="rounded bg-red-600 px-2 py-0.5 font-semibold text-white hover:bg-red-700">
                      {t('loyalty.common.yes')}
                    </button>
                    <button type="button" onClick={() => setVoidAsk(false)}
                      className="text-gray-500 hover:underline">{t('loyalty.common.cancel')}</button>
                  </div>
                )
              )}

              {/* Movement history */}
              {card.transactions?.length > 0 && (
                <div className="pt-2">
                  <div className="text-xs font-medium text-gray-500 mb-1">History</div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {card.transactions.map((tx) => (
                      <div key={tx.id} className="flex justify-between gap-2 text-xs text-gray-600">
                        <span>{tx.kind_label}{tx.order_number ? ` · ${tx.order_number}` : ''}</span>
                        <span className={`tabular-nums font-medium ${Number(tx.amount) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {money(tx.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Sell dialog */}
      {issueOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !issueBusy && setIssueOpen(false)}>
          <Card className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('loyalty.gc.sell')}</h2>
            <form onSubmit={issue} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-gray-500">Value (Rs) *</label>
                  <Input type="number" step="0.01" min="1" required value={form.face_value}
                    onChange={(e) => setForm({ ...form, face_value: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Paid (if different)</label>
                  <Input type="number" step="0.01" min="0" value={form.sold_amount}
                    onChange={(e) => setForm({ ...form, sold_amount: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Buyer name" value={form.purchaser_name}
                  onChange={(e) => setForm({ ...form, purchaser_name: e.target.value })} />
                <Input placeholder="Buyer phone" value={form.purchaser_phone}
                  onChange={(e) => setForm({ ...form, purchaser_phone: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="For (name)" value={form.recipient_name}
                  onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
                <Input placeholder="Their phone" value={form.recipient_phone}
                  onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} />
              </div>
              <Input placeholder="Message on the card" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-gray-500">Paid by</label>
                  <select className="w-full rounded-md border px-3 py-2 text-sm bg-background"
                    value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                    {['CASH', 'CARD', 'JUICE', 'MYT', 'OTHER'].map((mth) => (
                      <option key={mth} value={mth}>{mth}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Valid for (days, 0 = ∞)</label>
                  <Input type="number" min="0" value={form.validity_days}
                    onChange={(e) => setForm({ ...form, validity_days: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" disabled={issueBusy}
                  onClick={() => setIssueOpen(false)}>{t('loyalty.common.cancel')}</Button>
                <Button type="submit" disabled={issueBusy}
                  className="bg-amber-600 hover:bg-amber-700 text-white">{t('loyalty.gc.sell')}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
